import { lazyWithRetry } from "utils/lazyWithRetry";
import { usePermissions } from "context/PermissionContext";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import BusinessTypeProtectedRoute from "./BusinessTypeProtectedRoute";
import { ROLE } from "utils/constants";
import Dashboard from "features/SubClient/Dashboard";
import Profile from "features/UserProfile";
import { ProtectedElement } from "./ProtectedElement";
import LazyLoader from "components/lazyLoader";
import AppLayout from "layouts/AppLayout/AppLayout";
import { useAuth } from "context/AuthContext";
import { BusinessType } from "shared/types";
import ChangeAccountGroup from "features/SubClient/AccountManagement/Accounts/CustomerDetails/Sections/ChangeAccountGroup";

const DistributionTypes = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DistributionType/DistributionTypes"
    ),
);
const MaterialTypes = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/MaterialType/MaterialTypes"),
);
const VehicleTypes = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/VehicleType/VehicleTypes"),
);
const TaxOrFeeTypes = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/TaxOrFeeType/TaxOrFeeTypes"),
);
const RateTypes = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/RateType/RateTypes"),
);
const ContainerTypes = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/ContainerType/ContainerTypes"),
);
const ContainerTypeOptions = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/ContainerTypeOptions/ContainerTypeOptions"
    ),
);
const JurisdictionTypes = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/JurisdictionType/JurisdictionTypes"
    ),
);
const ServiceTypes = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ServiceType/ServiceTypes"),
);
const CommodityTypes = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/CommodityType/CommodityTypes"),
);
const SizeModelTypes = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/SizeModelType/SizeModelTypes"),
);
const RateDetailDescriptionTypes = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/RateDetailDescriptionType/RateDetailDescriptionTypes"
    ),
);

const ScaleTicketMessage = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/ScaleTicketMessage/ScaleTicketMessage"
    ),
);
const WeighIn = lazyWithRetry(
  () => import("features/SubClient/WeighMaster/WeighIn"),
);
const PendingWeighOuts = lazyWithRetry(
  () => import("features/SubClient/WeighMaster/PendingWeighOuts"),
);
const WeighOut = lazyWithRetry(
  () => import("features/SubClient/WeighMaster/WeighOut"),
);

const AddPayment = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Transactions/AddPayment"),
);

const ARTransactionHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Transactions/ARTransactionHistory"
    ),
);

const UserActionHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/LogsAndHistory/UserActionHistory/UserActionHistory"
    ),
);

const Accounts = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts"),
);
const CustomerDetails = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/CustomerDetails"),
);

const AddAccount = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/AddAccount"),
);
const EditAccountDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/EditAccountDetailsSetup"
    ),
);

const EditServiceAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/ServiceAddress/EditServiceAddress"
    ),
);

const AddBillingAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/BillingAddress/AddBillingAddress"
    ),
);
const EditBillingAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/BillingAddress/EditBillingAddress"
    ),
);

const AccountNotes = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/AccountNotes"),
);
const AddAccountNotes = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/AccountNotes/AddAccountNotes"
    ),
);
const EditAccountNotes = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/AccountNotes/EditAccountNotes"
    ),
);

const EditOwnerAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/OwnerAddress/EditOwnerAddress"
    ),
);
const AddOwnerAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/OwnerAddress/AddOwnerAddress"
    ),
);

const AddServiceDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/ServiceDetail/ServiceDetail"
    ),
);

const ViewActivityLog = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/ServiceDetail/ViewActivityLog"
    ),
);

const EditServiceDetailsScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/ServiceDetail/EditServiceDetailScheduleChange"
    ),
);
const ViewServiceDetailMasterScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/ServiceDetail/ViewServiceDetailMasterScheduleChange"
    ),
);

const AccountActionLogs = lazyWithRetry(
  () =>
    import("features/SubClient/AccountManagement/Accounts/AccountActionLogs"),
);
const AddDocuments = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Documents/AddDocuments"
    ),
);
const EditDocuments = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Documents/EditDocuments"
    ),
);
const Documents = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/Documents"),
);

const AddBankContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/AddBankContact"
    ),
);
const EditBankContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/EditbankContact"
    ),
);

const AddInsuranceContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/AddInsuranceContact"
    ),
);
const EditInsuranceContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/EditInsuranceContact"
    ),
);

const AddBondContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/AddBondContact"
    ),
);
const EditBondContact = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ContactDetails/EditBondContact"
    ),
);
const AccountEmailLogs = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/AccountEmailLogs/List"
    ),
);

const AddDistributionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DistributionType/AddDistributionType"
    ),
);
const EditDistributionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DistributionType/EditDistributionType"
    ),
);

const AddMaterialType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/MaterialType/AddMaterialType"),
);
const EditMaterialType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/MaterialType/EditMaterialType"),
);

const AddJurisdictionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/JurisdictionType/AddJurisdictionType"
    ),
);
const EditJurisdictionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/JurisdictionType/EditJurisdictionType"
    ),
);

const AddVehicleType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/VehicleType/AddVehicleType"),
);
const EditVehicleType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/VehicleType/EditVehicleType"),
);

const AddTaxOrFeeType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/TaxOrFeeType/AddTaxOrFeeType"),
);
const EditTaxOrFeeType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/TaxOrFeeType/EditTaxOrFeeType"),
);

const AddRateType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/RateType/AddRateType"),
);
const EditRateType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/RateType/EditRateType"),
);

const AddContainerType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/ContainerType/AddContainerType"),
);
const EditContainerType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/ContainerType/EditContainerType"),
);

const AddContainerTypeOption = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/ContainerTypeOptions/AddContainerTypeOption"
    ),
);
const EditContainerTypeOption = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/ContainerTypeOptions/EditContainerTypeOption"
    ),
);

const AddServiceType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ServiceType/AddServiceType"),
);
const EditServiceType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ServiceType/EditServiceType"),
);

const CopyMasterType = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/CopyMasters/CopyMasterType"),
);

const AddCommodityType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/CommodityType/AddCommodityType"),
);

const EditCommodityType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/CommodityType/EditCommodityType"),
);

const AddSizeModelType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/SizeModelType/AddSizeModelType"),
);

const EditSizeModelType = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/SizeModelType/EditSizeModelType"),
);

const AddRateDetailDescriptionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/RateDetailDescriptionType/AddRateDetailDescriptionType"
    ),
);
const EditRateDetailDescriptionType = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/RateDetailDescriptionType/EditRateDetailDescriptionType"
    ),
);

const DispatchAddress = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/DispatchAddress"),
);
const AddDispatchAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DispatchAddress/AddDispatchAddress"
    ),
);
const EditDispatchAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DispatchAddress/EditDispatchAddress"
    ),
);
const CopyDispatchAddress = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/DispatchAddress/CopyDispatchAddress"
    ),
);

const RateDetailList = lazyWithRetry(
  () => import("features/SubClient/RateManagement/RegularRateMaintenance"),
);

const AddRegularRateMaintenance = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/AddRegularRate"
    ),
);

const EditRegularRateMaintenance = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/EditRegularRate"
    ),
);

const RegularRateViewHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/RegularRateViewHistory"
    ),
);

const AddScheduledRegularRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/AddScheduledRegularRate"
    ),
);

const EditScheduledRegularRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/EditScheduledRegularRate"
    ),
);

const ViewHistoricRegularRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/ViewHistoricRegularRate"
    ),
);

const CloneRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateMaintenance/CloneRate"
    ),
);

const CustomRates = lazyWithRetry(
  () => import("features/SubClient/RateManagement/CustomRates"),
);

const AccountGroups = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/AccountGroup"),
);
const AddAccountGroup = lazyWithRetry(
  () =>
    import("features/SubClient/SystemSettings/AccountGroup/AccountGroupSetup"),
);

const BillingGroups = lazyWithRetry(
  () => import("features/SubClient/Billing/BillingGroups"),
);
const AddBillingGroup = lazyWithRetry(
  () => import("features/SubClient/Billing/BillingGroups/AddBillingGroup"),
);

const EditBillingGroup = lazyWithRetry(
  () => import("features/SubClient/Billing/BillingGroups/EditBillingGroup"),
);

const JurisdictionDetailsList = lazyWithRetry(
  () => import("features/SubClient/RateManagement/JurisdictionDetails/List"),
);
const AddJurisdictionDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/AddJurisdictionDetails"
    ),
);
const EditJurisdictionDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/EditJurisdictionDetails"
    ),
);
const JurisdictionDetailsUpdateHistoryList = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/JurisdictionDetailsUpdateHistoryList"
    ),
);
const AddScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/AddScheduleChange"
    ),
);
const EditScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/EditScheduleChange"
    ),
);
const ViewHistoricJurisdictionDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/JurisdictionDetails/ViewHistoricJurisdictionDetails"
    ),
);

const ContainerWeightLimitList = lazyWithRetry(
  () => import("features/SubClient/RateManagement/ContainerWeightLimit/List"),
);
const AddContainerWeightLimit = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/AddContainerWeightLimit"
    ),
);
const EditContainerWeightLimit = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/EditContainerWeightLimit"
    ),
);
const ContainerWeightLimitHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/ContainerWeightLimitUpdateHistory"
    ),
);
const AddContainerWeightLimitScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/AddContainerWeightLimitScheduleChange"
    ),
);
const EditContainerWeightLimitScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/EditContainerWeightLimitScheduleChange"
    ),
);
const ViewHistoricContainerWeightLimit = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightLimit/ViewHistoricContainerWeightLimit"
    ),
);

const WeightRateList = lazyWithRetry(
  () => import("features/SubClient/RateManagement/ContainerWeightRate/List"),
);
const AddMaterialWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/AddMaterialWeightRate"
    ),
);
const AddCommodityWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/AddCommodityWeightRate"
    ),
);
const EditCommodityWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/EditCommodityWeightRate"
    ),
);
const EditMaterialWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/EditMaterialWeightRate"
    ),
);
const AddScheduleChangeMaterialWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/AddScheduleChangeMaterialWeightRate"
    ),
);
const EditScheduleChangeMaterialWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/EditScheduleChangeMaterialWeightRate"
    ),
);
const MaterialWeightRateUpdateHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/MaterialWeightRateUpdateHistory"
    ),
);
const CommodityWeightRateUpdateHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/CommodityWeightRateUpdateHistory"
    ),
);
const ViewHistoricMaterialWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/MaterialWeightRate/ViewHistoricMaterialWeightRate"
    ),
);
const AddCommodityWeightRateScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/AddCommodityWeightRateScheduleChange"
    ),
);
const EditCommodityWeightRateScheduleChange = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/EditCommodityWeightRateScheduleChange"
    ),
);
const ViewHistoricCommodityWeightRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/ContainerWeightRate/CommodityWeightRate/ViewCommodityWeightRateHistoric"
    ),
);

const RateBulkUpload = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/RegularRateBulkUpload/RateBulkUpload"
    ),
);

const SpecialServiceRateDetailList = lazyWithRetry(
  () => import("features/SubClient/RateManagement/SpecialServiceRate"),
);

const AddSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/AddSpecialServiceRate"
    ),
);
const EditSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/EditSpecialServiceRate"
    ),
);
const AddScheduleChangeSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/AddScheduleSpecialServiceRate"
    ),
);
const ViewHistorySpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/SpecialServiceRateUpdateHistory"
    ),
);
const EditScheduleChangeSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/EditScheduleSpecialServiceRate"
    ),
);
const CloneSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/CloneSpecialServiceRate"
    ),
);
const ViewHistoricSpecialServiceRate = lazyWithRetry(
  () =>
    import(
      "features/SubClient/RateManagement/SpecialServiceRate/ViewHistoricSpecialServiceRate"
    ),
);

const SpecialServiceTickets = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/SpecialServiceTickets"),
);
const AddSpecialServiceTicket = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/AddSpecialServiceTicket"
    ),
);
const EditSpecialServiceTicket = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/EditSpecialServiceTicket"
    ),
);
const ViewSpecialServiceTicket = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/ViewSpecialServiceTicket"
    ),
);
const SpecialServiceTicketHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/SpecialServiceTicketHistory"
    ),
);
const CloseTicket = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/CloseTicket"
    ),
);
const ViewCloseTicket = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/SpecialServiceTickets/CloseTicket/ViewCloseTicket"
    ),
);

const AddMeterDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/WaterMeterReading/Components/WaterMeterSetup"
    ),
);
const EditMeterDetails = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/WaterMeterReading/Components/WaterMeterSetup"
    ),
);
const WaterMeterHistory = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/WaterMeterReading/WaterMeterHistory"
    ),
);

const Transactions = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Transactions"),
);
const AddARTransactions = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Transactions/AddMiscellenousARTransactions"
    ),
);
const ViewMiscellenousARTransactions = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Transactions/ViewMiscellenousARTransactions"
    ),
);
const ViewCreditDebitARTransactions = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Transactions/ViewCreditDebitARTransaction"
    ),
);
const ViewPaymentARTransactions = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Transactions/ViewPaymentARTransaction"
    ),
);

const AddStatementMessage = lazyWithRetry(
  () =>
    import("features/SubClient/Billing/StatementMessage/AddStatementMessage"),
);

const BillingBatches = lazyWithRetry(
  () => import("features/SubClient/Billing/BillingBatch"),
);
const BillingBatch = lazyWithRetry(
  () => import("features/SubClient/Billing/BillingBatch/BillingBatchSetup"),
);
const ViewHistoryBillingBatch = lazyWithRetry(
  () =>
    import("features/SubClient/Billing/BillingBatch/ViewHistoryBillingBatch"),
);

const ScheduleAccountList = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ScheduleAccountStatus"
    ),
);
const AddScheduleAccountStatus = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ScheduleAccountStatus/AddScheduleAccountStatus"
    ),
);
const EditScheduleAccountStatus = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ScheduleAccountStatus/EditScheduleAccountStatus"
    ),
);
const ViewScheduleAccountStatus = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/ScheduleAccountStatus/ViewScheduledAccountStatus"
    ),
);

const VehicleDetailsList = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/VehicleDetails"),
);
const AddVehicleDetail = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/VehicleDetails/AddVehicleDetail"
    ),
);
const ViewVehicleDetail = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/VehicleDetails/ViewVehicleDetail"
    ),
);

const WeighMasterScaleTicketsList = lazyWithRetry(
  () => import("features/SubClient/WeighMaster"),
);

const ViewWeighMasterClosedTicket = lazyWithRetry(
  () => import("features/SubClient/WeighMaster/ViewClosedTicket"),
);

const AddCompliance = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/Compliance/AddCompliance"
    ),
);
const EditCompliance = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/Compliance/EditCompliance"
    ),
);
const ViewCompliance = lazyWithRetry(
  () =>
    import(
      "features/SubClient/AccountManagement/Accounts/Hauling/Compliance/ViewCompliance"
    ),
);

const PrintTicketsBatchList = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/SpecialServiceTicketPrint/PrintTicketsBatchList"
    ),
);

const PrintSpecialServiceTicketsList = lazyWithRetry(
  () =>
    import(
      "features/SubClient/SystemSettings/SpecialServiceTicketPrint/PrintSpecialServiceTicketList"
    ),
);

const RouteSheets = lazyWithRetry(
  () => import("features/SubClient/Reports/RouteSheets"),
);

const Letters = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ManageLetters"),
);
const AddLetter = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ManageLetters/AddLetter"),
);
const ViewLetter = lazyWithRetry(
  () => import("features/SubClient/SystemSettings/ManageLetters/ViewLetter"),
);

const CustomerOptions = lazyWithRetry(
  () => import("features/SubClient/AccountManagement/Accounts/CustomerOptions"),
);

const SystemReports = lazyWithRetry(
  () => import("features/SubClient/Reports/SystemReports"),
);

const ImportExport = lazyWithRetry(
  () => import("features/SubClient/ImportExport"),
);

const systemSettingsRoutes = [
  {
    path: "distribution-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <DistributionTypes /> },
      { path: "add", element: <AddDistributionType /> },
      { path: ":distributionTypeId", element: <EditDistributionType /> },
    ],
  },
  {
    path: "material-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <MaterialTypes /> },
      { path: "add", element: <AddMaterialType /> },
      { path: ":materialTypeId", element: <EditMaterialType /> },
    ],
  },
  {
    path: "vehicle-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <VehicleTypes /> },
      { path: "add", element: <AddVehicleType /> },
      { path: ":vehicleTypeId", element: <EditVehicleType /> },
    ],
  },
  {
    path: "tax-fee-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <TaxOrFeeTypes /> },
      { path: "add", element: <AddTaxOrFeeType /> },
      { path: ":taxFeeTypeId", element: <EditTaxOrFeeType /> },
    ],
  },
  {
    path: "rate-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <RateTypes /> },
      { path: "add", element: <AddRateType /> },
      { path: ":rateTypeId", element: <EditRateType /> },
    ],
  },
  {
    path: "container-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <ContainerTypes /> },
      { path: "add", element: <AddContainerType /> },
      { path: ":containerTypeId", element: <EditContainerType /> },
    ],
  },
  {
    path: "container-type-options",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <ContainerTypeOptions /> },
      { path: "add", element: <AddContainerTypeOption /> },
      {
        path: ":containerTypeOptionId",
        element: <EditContainerTypeOption />,
      },
    ],
  },
  {
    path: "jurisdictions",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <JurisdictionTypes /> },
      { path: "add", element: <AddJurisdictionType /> },
      { path: ":jurisdictionId", element: <EditJurisdictionType /> },
    ],
  },
  {
    path: "service-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <ServiceTypes /> },
      { path: "add", element: <AddServiceType /> },
      { path: ":serviceTypeId", element: <EditServiceType /> },
      { path: "view/:serviceTypeId", element: <EditServiceType /> },
    ],
  },
  {
    path: "commodity-types",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <CommodityTypes /> },
      { path: "add", element: <AddCommodityType /> },
      { path: ":commodityTypeId", element: <EditCommodityType /> },
    ],
  },
  {
    path: "container-size-models",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <SizeModelTypes /> },
      { path: "add", element: <AddSizeModelType /> },
      { path: ":sizeModelId", element: <EditSizeModelType /> },
      { path: "view/:sizeModelId", element: <EditSizeModelType /> },
    ],
  },
  {
    path: "rate-detail-descriptions",
    permission: "MANAGE_MASTERS",
    routes: [
      { index: true, element: <RateDetailDescriptionTypes /> },
      { path: "add", element: <AddRateDetailDescriptionType /> },
      {
        path: ":rateDetailDescId",
        element: <EditRateDetailDescriptionType />,
      },
    ],
  },
  {
    path: "copy-master",
    permission: "MANAGE_MASTERS",
    routes: [{ index: true, element: <CopyMasterType /> }],
  },
  {
    path: "dispatch-address",
    permission: "MANAGE_DISPATCH_ADDRESS_MASTERS",
    allowedFor: "hauling",
    routes: [
      { index: true, element: <DispatchAddress /> },
      { path: "add", element: <AddDispatchAddress /> },
      { path: ":dispatchAddressId", element: <EditDispatchAddress /> },
      { path: "copy", element: <CopyDispatchAddress /> },
    ],
  },
];

const SubClientRoutes = () => {
  const { subClientPermissions } = usePermissions();
  const { isTransferStationBusinessTypeEnabled } = useAuth();

  const mainRatePermission = isTransferStationBusinessTypeEnabled
    ? "TRANSFER_STATION_RATE_MAINTENANCE"
    : "REGULAR_RATE_MAINTENANCE";

  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute roles={[ROLE.subClient]}>
            {/* <IdleTimeoutProvider> */}
            <AppLayout />
            {/* </IdleTimeoutProvider> */}
          </ProtectedRoute>
        }
      >
        <Route
          path="dashboard"
          element={
            <LazyLoader>
              <Dashboard />
            </LazyLoader>
          }
        />
        <Route
          path="log-history"
          element={
            <ProtectedElement
              element={<Outlet />}
              requiredPermissions={["USER_ACTION_HISTORY"]}
              assignedPermissions={subClientPermissions}
            />
          }
        >
          <Route path="user" element={<UserActionHistory />} />
        </Route>

        <Route path="account-management">
          <Route
            path="accounts"
            element={
              <BusinessTypeProtectedRoute allowedFor="both">
                <ProtectedElement
                  requiredPermissions={["CUSTOMER_FILE_MAINTENANCE"]}
                  element={<Outlet />}
                  assignedPermissions={subClientPermissions}
                />
              </BusinessTypeProtectedRoute>
            }
          >
            <Route index element={<Accounts />} />
            <Route path="add-account" element={<AddAccount />} />
            <Route path="customer-details">
              <Route index element={<CustomerDetails />} />
              <Route
                path="change-account-group"
                element={<ChangeAccountGroup />}
              />

              <Route path="compliance">
                <Route
                  path="add"
                  element={
                    <ProtectedElement
                      requiredPermissions={["CFM_MANAGE_COMPLIANCE"]}
                      element={<AddCompliance />}
                      assignedPermissions={subClientPermissions}
                    />
                  }
                />
                <Route
                  path="edit"
                  element={
                    <ProtectedElement
                      requiredPermissions={["CFM_MANAGE_COMPLIANCE"]}
                      element={<EditCompliance />}
                      assignedPermissions={subClientPermissions}
                    />
                  }
                />

                <Route path="view" element={<ViewCompliance />} />
              </Route>

              <Route path="vehicle-details">
                <Route index element={<VehicleDetailsList />} />
                <Route
                  path="add"
                  element={
                    <ProtectedElement
                      requiredPermissions={["CFM_MANAGE_VEHICLE_DETAIL"]}
                      element={<AddVehicleDetail />}
                      assignedPermissions={subClientPermissions}
                    />
                  }
                />
                <Route
                  path="view/:vehicleDetailId"
                  element={<ViewVehicleDetail />}
                />
              </Route>

              <Route path="meter-details">
                <Route
                  path="add"
                  element={
                    <ProtectedElement
                      requiredPermissions={["CFM_MANAGE_WATER_METER"]}
                      element={<AddMeterDetails />}
                      assignedPermissions={subClientPermissions}
                    />
                  }
                />
                <Route path="edit" element={<EditMeterDetails />} />
                <Route path="history" element={<WaterMeterHistory />} />
              </Route>

              <Route path="ar-history" element={<ARTransactionHistory />} />
              <Route
                path="edit-account-details"
                element={<EditAccountDetails />}
              />

              <Route
                path="edit-service-address"
                element={<EditServiceAddress />}
              />
              <Route path="add-service-details">
                <Route index element={<AddServiceDetails />} />
                <Route
                  path="edit-service-address"
                  element={<EditServiceAddress />}
                />
                <Route path="view-activity-log" element={<ViewActivityLog />} />
                <Route path="edit-schedule-change">
                  <Route index element={<EditServiceDetailsScheduleChange />} />
                  <Route
                    path="view-activity-log"
                    element={<ViewActivityLog />}
                  />
                </Route>
                <Route path="view-schedule-change">
                  <Route
                    index
                    element={<ViewServiceDetailMasterScheduleChange />}
                  />
                  <Route
                    path="view-activity-log"
                    element={<ViewActivityLog />}
                  />
                </Route>
              </Route>
              <Route
                path="add-billing-address"
                element={<AddBillingAddress />}
              />
              <Route
                path="edit-billing-address"
                element={<EditBillingAddress />}
              />
              <Route path="add-owner-address" element={<AddOwnerAddress />} />
              <Route path="edit-owner-address" element={<EditOwnerAddress />} />
              <Route
                path="account-action-logs"
                element={<AccountActionLogs />}
              />
              <Route path="add-account-note" element={<AddAccountNotes />} />
              <Route path="account-notes">
                <Route index element={<AccountNotes />} />
                <Route path="add" element={<AddAccountNotes />} />
                <Route path="edit/:commentId" element={<EditAccountNotes />} />
              </Route>
              <Route path="account-email-logs">
                <Route index element={<AccountEmailLogs />} />
              </Route>
              <Route path="contacts">
                <Route path="bank">
                  <Route path="add" element={<AddBankContact />} />
                  <Route path="edit" element={<EditBankContact />} />
                </Route>
                <Route path="insurance">
                  <Route path="add" element={<AddInsuranceContact />} />
                  <Route path="edit" element={<EditInsuranceContact />} />
                </Route>
                <Route path="bond">
                  <Route path="add" element={<AddBondContact />} />
                  <Route path="edit" element={<EditBondContact />} />
                </Route>
              </Route>
              <Route path="documents">
                <Route index element={<Documents />} />
                <Route path="add" element={<AddDocuments />} />
                <Route path="edit" element={<EditDocuments />} />
              </Route>
              <Route path="transactions" element={<Transactions />} />
              <Route
                path="special-service-tickets"
                element={<SpecialServiceTickets />}
              />
              <Route path="schedule-account-list">
                <Route index element={<ScheduleAccountList />} />
                <Route path="add" element={<AddScheduleAccountStatus />} />
                <Route
                  path=":scheduleStatusId/edit"
                  element={<EditScheduleAccountStatus />}
                />
                <Route
                  path=":scheduleStatusId/view"
                  element={<ViewScheduleAccountStatus />}
                />
              </Route>
              <Route path="customer-options" element={<CustomerOptions />} />
              <Route
                path="weigh-master"
                element={
                  <ProtectedElement
                    requiredPermissions={["MANAGE_WEIGH_MASTER"]}
                    element={<Outlet />}
                    assignedPermissions={subClientPermissions}
                  />
                }
              >
                <Route index element={<WeighMasterScaleTicketsList />} />
                <Route
                  path="view/:scaleTicketNumber"
                  element={<ViewWeighMasterClosedTicket />}
                />
              </Route>
            </Route>
          </Route>

          <Route
            path="special-service-tickets"
            element={
              <BusinessTypeProtectedRoute allowedFor="hauling">
                <LazyLoader>
                  <Outlet />
                </LazyLoader>
              </BusinessTypeProtectedRoute>
            }
          >
            <Route index element={<SpecialServiceTickets />} />
            <Route
              path="add"
              element={
                <ProtectedElement
                  requiredPermissions={[
                    "MANAGE_SPECIAL_SERVICE_TICKET",
                    "CREATE_TICKET",
                  ]}
                  element={<Outlet />}
                  assignedPermissions={subClientPermissions}
                />
              }
            >
              <Route index element={<AddSpecialServiceTicket />} />

              <Route
                path="payment"
                element={<AddPayment feature="special-service-ticket" />}
              />
            </Route>

            <Route
              path="history/:ticketNumber"
              element={<SpecialServiceTicketHistory />}
            />

            <Route path="view">
              <Route
                path=":ticketNumber"
                element={<ViewSpecialServiceTicket />}
              />
              <Route path="close-ticket" element={<CloseTicket />} />
            </Route>
            <Route path="view-close-ticket" element={<ViewCloseTicket />} />
            <Route
              path="edit/:ticketNumber"
              element={
                <ProtectedElement
                  requiredPermissions={[
                    "MANAGE_SPECIAL_SERVICE_TICKET",
                    "EDIT_TICKET",
                  ]}
                  element={<EditSpecialServiceTicket />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="special-service-ticket-print"
              element={
                <BusinessTypeProtectedRoute allowedFor="hauling">
                  <ProtectedElement
                    requiredPermissions={["PRINT_TICKET", "RE_PRINT_TICKET"]}
                    element={<Outlet />}
                    assignedPermissions={subClientPermissions}
                    matchAll={false}
                  />
                </BusinessTypeProtectedRoute>
              }
            >
              <Route index element={<PrintTicketsBatchList />} />

              <Route
                path=":batchNumber"
                element={<PrintSpecialServiceTicketsList />}
              />
            </Route>
          </Route>

          <Route
            path="transactions"
            element={
              <LazyLoader>
                <Outlet />
              </LazyLoader>
            }
          >
            <Route index element={<Transactions />} />
            <Route
              path="add"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<AddARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="miscellaneous-charge/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewMiscellenousARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="miscellaneous-charge-special-service/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewMiscellenousARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="credit/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewCreditDebitARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="debit/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewCreditDebitARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="prorate-credit/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewCreditDebitARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="prorate-debit/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewCreditDebitARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
            <Route
              path="payment/view/:transactionId"
              element={
                <ProtectedElement
                  requiredPermissions={["MANAGE_AR_TRANSACTIONS"]}
                  element={<ViewPaymentARTransactions />}
                  assignedPermissions={subClientPermissions}
                />
              }
            />
          </Route>
        </Route>

        <Route
          path="weigh-master"
          element={
            <BusinessTypeProtectedRoute allowedFor="transfer-station">
              <ProtectedElement
                requiredPermissions={["MANAGE_WEIGH_MASTER"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            </BusinessTypeProtectedRoute>
          }
        >
          <Route index element={<WeighIn />} />
          <Route
            path="payment"
            element={<AddPayment feature="weigh-master" />}
          />
          <Route path="pending-weigh-outs" element={<Outlet />}>
            <Route index element={<PendingWeighOuts />} />
            <Route
              path="closed/:scaleTicketNumber"
              element={<ViewWeighMasterClosedTicket />}
            />
            <Route path=":scaleTicketNumber" element={<WeighOut />} />
          </Route>
        </Route>

        <Route path="system-settings">
          <Route
            path="account-groups"
            element={
              <ProtectedElement
                requiredPermissions={["MANAGE_ACCOUNT_GROUPS"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<AccountGroups />} />
            <Route path="add" element={<AddAccountGroup />} />
          </Route>
          <Route
            path="scale-ticket-message"
            element={
              <BusinessTypeProtectedRoute allowedFor="transfer-station">
                <ProtectedElement
                  requiredPermissions={["MANAGE_SCALE_TICKET_MESSAGE"]}
                  element={<Outlet />}
                  assignedPermissions={subClientPermissions}
                />
              </BusinessTypeProtectedRoute>
            }
          >
            <Route index element={<ScaleTicketMessage />} />
          </Route>
          <Route
            path="manage-letters"
            element={
              <ProtectedElement
                requiredPermissions={["MANAGE_LETTERS"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<Letters />} />
            <Route path="add" element={<AddLetter />} />
            <Route path="view/:letterId" element={<ViewLetter />} />
          </Route>
          {systemSettingsRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <BusinessTypeProtectedRoute
                  allowedFor={(route?.allowedFor || "both") as BusinessType}
                >
                  <ProtectedElement
                    element={<Outlet />}
                    requiredPermissions={[route.permission]}
                    assignedPermissions={subClientPermissions}
                  />
                </BusinessTypeProtectedRoute>
              }
            >
              {route.routes.map((subRoute, index) => (
                <Route
                  key={index}
                  index={subRoute.index}
                  path={subRoute.path}
                  element={<LazyLoader>{subRoute.element}</LazyLoader>}
                />
              ))}
            </Route>
          ))}
        </Route>

        <Route path="rate-management">
          <Route
            path="regular-rates"
            element={
              <ProtectedElement
                element={<Outlet />}
                requiredPermissions={[
                  "REGULAR_RATE_MAINTENANCE",
                  "MANAGE_RATE_MAINTENANCE",
                ]}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<RateDetailList />} />
            <Route path="view-historic-regular-rate">
              <Route index element={<ViewHistoricRegularRate />} />
              <Route
                path="rate-history/:regularRateCode"
                element={<RegularRateViewHistory />}
              />
            </Route>
            <Route path="edit-regular-rate-scheduled-change/:regularRateCode">
              <Route index element={<EditScheduledRegularRate />} />
              <Route
                path="rate-history/:regularRateCode"
                element={<RegularRateViewHistory />}
              />
            </Route>
            <Route path="add" element={<AddRegularRateMaintenance />} />
            <Route path=":regularRateCode">
              <Route index element={<EditRegularRateMaintenance />} />
              <Route path="clone-to" element={<CloneRate />} />
              <Route
                path="rate-history/:regularRateCode"
                element={<RegularRateViewHistory />}
              />
              <Route path="add-scheduled-regular-rate">
                <Route index element={<AddScheduledRegularRate />} />
                <Route
                  path="rate-history/:regularRateCode"
                  element={<RegularRateViewHistory />}
                />
              </Route>
            </Route>
          </Route>
          <Route
            path="bulk-upload"
            element={
              <ProtectedElement
                element={<RateBulkUpload />}
                requiredPermissions={[
                  "MANAGE_RATE_MAINTENANCE",
                  "REGULAR_RATE_MAINTENANCE",
                ]}
                assignedPermissions={subClientPermissions}
              />
            }
          />
          <Route
            path="custom-rates"
            element={
              <ProtectedElement
                element={<CustomRates />}
                requiredPermissions={[
                  "REGULAR_RATE_MAINTENANCE",
                  "CUSTOM_RATE_SEARCH",
                ]}
                assignedPermissions={subClientPermissions}
                matchAll={false}
              />
            }
          />
          <Route
            path="container-minimum-weights"
            element={
              <ProtectedElement
                element={<Outlet />}
                requiredPermissions={[
                  "REGULAR_RATE_MAINTENANCE",
                  "MANAGE_MINIMUM_WEIGHT_AND_RATE",
                ]}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<ContainerWeightLimitList />} />
            <Route path="add" element={<AddContainerWeightLimit />} />
            <Route
              path="edit/:accountGroupId"
              element={<EditContainerWeightLimit />}
            />
            <Route
              path="history/:accountGroupId"
              element={<ContainerWeightLimitHistory />}
            />
            <Route
              path="schedule-change/:accountGroupId"
              element={<AddContainerWeightLimitScheduleChange />}
            />
            <Route
              path="edit-schedule-change/:accountGroupId"
              element={<EditContainerWeightLimitScheduleChange />}
            />
            <Route
              path="historic/:accountGroupId"
              element={<ViewHistoricContainerWeightLimit />}
            />
          </Route>
          <Route
            path="weight-rates"
            element={
              <ProtectedElement
                element={<Outlet />}
                requiredPermissions={[
                  "REGULAR_RATE_MAINTENANCE",
                  "MANAGE_MINIMUM_WEIGHT_AND_RATE",
                ]}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route path="commodity">
              <Route index element={<Navigate to="add" replace />} />
              <Route path="add" element={<AddCommodityWeightRate />} />
              <Route
                path="edit/:accountGroupId"
                element={<EditCommodityWeightRate />}
              />
              <Route
                path="schedule-change/:accountGroupId"
                element={<AddCommodityWeightRateScheduleChange />}
              />

              <Route
                path="edit-schedule-change/:accountGroupId"
                element={<EditCommodityWeightRateScheduleChange />}
              />

              <Route
                path="history/:accountGroupId"
                element={<CommodityWeightRateUpdateHistory />}
              />

              <Route
                path="historic/:accountGroupId"
                element={<ViewHistoricCommodityWeightRate />}
              />
            </Route>
            <Route path="material">
              <Route index element={<Navigate to="add" replace />} />
              <Route path="add" element={<AddMaterialWeightRate />} />
              <Route
                path="edit/:accountGroupId"
                element={<EditMaterialWeightRate />}
              />
              <Route
                path="schedule-change/:accountGroupId"
                element={<AddScheduleChangeMaterialWeightRate />}
              />
              <Route
                path="edit-schedule-change/:accountGroupId"
                element={<EditScheduleChangeMaterialWeightRate />}
              />
              <Route
                path="history/:accountGroupId"
                element={<MaterialWeightRateUpdateHistory />}
              />
              <Route
                path="historic/:accountGroupId"
                element={<ViewHistoricMaterialWeightRate />}
              />
            </Route>
            <Route index element={<WeightRateList />} />
          </Route>
          <Route
            path="jurisdiction-details"
            element={
              <ProtectedElement
                requiredPermissions={[
                  mainRatePermission,
                  "MANAGE_JURISDICTION_DETAILS",
                ]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<JurisdictionDetailsList />} />
            <Route path="view-historic-jurisdiction-detail">
              <Route index element={<ViewHistoricJurisdictionDetails />} />
            </Route>
            <Route path="edit-schedule-change">
              <Route index element={<EditScheduleChange />} />
            </Route>
            <Route path="add" element={<AddJurisdictionDetails />} />
            <Route path="edit">
              <Route index element={<EditJurisdictionDetails />} />
              <Route path="schedule-change">
                <Route index element={<AddScheduleChange />} />
              </Route>
            </Route>
            <Route
              path="history"
              element={<JurisdictionDetailsUpdateHistoryList />}
            />
          </Route>
          <Route
            path="special-service-rates"
            element={
              <ProtectedElement
                element={<Outlet />}
                requiredPermissions={[
                  "REGULAR_RATE_MAINTENANCE",
                  "MANAGE_SPECIAL_SERVICE_RATE",
                ]}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<SpecialServiceRateDetailList />} />
            <Route path="add" element={<AddSpecialServiceRate />} />
            <Route path="edit/:rateCode" element={<EditSpecialServiceRate />} />
            <Route
              path="add-schedule-change/:rateCode"
              element={<AddScheduleChangeSpecialServiceRate />}
            />
            <Route
              path="edit-schedule-change/:rateCode"
              element={<EditScheduleChangeSpecialServiceRate />}
            />
            <Route path="clone-to" element={<CloneSpecialServiceRate />} />
            <Route
              path="view-historic-special-service-rate"
              element={<ViewHistoricSpecialServiceRate />}
            />
            <Route
              path="rate-history/:rateCode"
              element={<ViewHistorySpecialServiceRate />}
            />
          </Route>
        </Route>
        <Route path="billing">
          <Route
            path="statement-message"
            element={
              <ProtectedElement
                requiredPermissions={["MANAGE_STATEMENT_MESSAGE"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<AddStatementMessage />} />
          </Route>
          <Route
            path="billing-batch"
            element={
              <ProtectedElement
                element={<Outlet />}
                requiredPermissions={["BILLING_BATCH"]}
                assignedPermissions={subClientPermissions}
                matchAll={false}
              />
            }
          >
            <Route index element={<BillingBatches />} />
            <Route
              path="view-history/:batchNumber"
              element={<ViewHistoryBillingBatch />}
            />
            <Route path="add" element={<BillingBatch />} />
          </Route>
          <Route
            path="billing-groups"
            element={
              <ProtectedElement
                requiredPermissions={["MANAGE_BILLING_GROUPS"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<BillingGroups />} />
            <Route path=":billingGroupId" element={<EditBillingGroup />} />
            <Route path="add" element={<AddBillingGroup />} />
          </Route>
        </Route>
        <Route path="reports">
          <Route
            path="route-sheets"
            element={
              <BusinessTypeProtectedRoute allowedFor="hauling">
                <ProtectedElement
                  requiredPermissions={["MANAGE_ROUTE_SHEET"]}
                  element={<Outlet />}
                  assignedPermissions={subClientPermissions}
                />
              </BusinessTypeProtectedRoute>
            }
          >
            <Route index element={<RouteSheets />} />
          </Route>
          <Route
            path="system-reports"
            element={
              <ProtectedElement
                requiredPermissions={["REPORTS"]}
                element={<Outlet />}
                assignedPermissions={subClientPermissions}
              />
            }
          >
            <Route index element={<SystemReports />} />
          </Route>
        </Route>
        <Route path="import-export" element={
          <BusinessTypeProtectedRoute allowedFor="hauling">
            <ProtectedElement
              requiredPermissions={["MANAGE_IMPORT_EXPORT"]}
              element={<Outlet />}
              assignedPermissions={subClientPermissions}
            />
          </BusinessTypeProtectedRoute>
        }>
          <Route index element={<ImportExport />} />
        </Route>
        <Route
          path="profile"
          element={
            <LazyLoader>
              <Profile />
            </LazyLoader>
          }
        />
        <Route path="*" element={<Navigate to="dashboard" />} />
      </Route>
    </Routes>
  );
};

export default SubClientRoutes;
